/**
 * Browser Fingerprint Utility for Try It Out (unauthenticated)
 *
 * Generates a stable browser fingerprint hash using @fingerprintjs/fingerprintjs
 * (already installed in the project). The fingerprint is cached in localStorage
 * so the same visitor always sends the same hash.
 *
 * The hash is sent to the backend via the X-TryItOut-Fingerprint header.
 */

const STORAGE_KEY = "tryitout_fingerprint";

/**
 * Get (or generate) the browser fingerprint hash.
 * Returns the FingerprintJS visitorId — a stable SHA-256-like identifier.
 * Falls back to a simple hash if FingerprintJS fails.
 */
export async function getFingerprint() {
  // Return cached value if available
  const cached = localStorage.getItem(STORAGE_KEY);
  if (cached) {
    return cached;
  }

  try {
    const FingerprintJS = await import("@fingerprintjs/fingerprintjs");
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    const visitorId = result.visitorId;

    localStorage.setItem(STORAGE_KEY, visitorId);
    return visitorId;
  } catch (err) {
    console.warn(
      "[fingerprint] FingerprintJS failed, using fallback:",
      err.message,
    );
    const fallback = await generateFallbackFingerprint();
    localStorage.setItem(STORAGE_KEY, fallback);
    return fallback;
  }
}

/**
 * Fallback fingerprint using canvas + WebGL + browser attributes.
 * Only used if FingerprintJS fails to load.
 */
async function generateFallbackFingerprint() {
  const components = [
    navigator.userAgent,
    navigator.language,
    navigator.platform,
    new Date().getTimezoneOffset().toString(),
    window.screen.width + "x" + window.screen.height,
    window.screen.colorDepth?.toString() || "",
    navigator.hardwareConcurrency?.toString() || "",
    navigator.deviceMemory?.toString() || "",
  ];

  // Canvas fingerprint
  try {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.textBaseline = "top";
    ctx.font = '14px "Arial"';
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("KinShield-TryItOut", 2, 15);
    components.push(canvas.toDataURL());
  } catch {
    // canvas not available
  }

  const raw = components.join("|");
  const encoded = new TextEncoder().encode(raw);
  const hashBuffer = await crypto.subtle.digest("SHA-256", encoded);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Clear stored fingerprint (for testing).
 */
export function clearFingerprint() {
  localStorage.removeItem(STORAGE_KEY);
}
